import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { usePageManager } from "web/react/components/feeds-filters/feeds-filters-pagination/pagination-state";
import { openFeedsFiltersOverlay } from "web/react/components/feeds-filters/hooks/use-overlay-handler";
import { ClearFilterButton } from "web/react/components/filters-emo/clear-filter-button/clear-filter-button";
import { Counter } from "web/react/components/filters-emo/counter";
import { useFilterContext } from "web/react/components/filters-emo/filter-container-emo";
import LazyModal from "web/react/components/lazy-modal/lazy-modal";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Button } from "web/react/emo/button";
import { Text } from "web/react/emo/text";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import { gettext } from "web/script/modules/django-i18n";
import { UniversalFilterOption } from "web/types/universal-filters";
import * as styles from "./overlay.css";

interface OverlayProps {
    isOpen: boolean;
    sectionTitle: string;
    closeCollapsable(): void;
    children: JSX.Element | null;
    canApply: boolean;
    cancelAndClose: () => void;
    cancelStateUpdate: () => void;
    applyAndClose: () => void;
    overlayIsOpen: boolean;
    filterBreadcrumb?: UniversalFilterOption[];
    isMoreFiltersSelected: boolean;
}

export function Overlay({
    sectionTitle,
    isOpen,
    children,
    closeCollapsable,
    canApply,
    applyAndClose,
    cancelAndClose,
    cancelStateUpdate,
    overlayIsOpen,
    filterBreadcrumb = [],
    isMoreFiltersSelected = false,
}: OverlayProps): JSX.Element | null {
    const { isTabletViewport } = useDomViewport();
    const { selectedFilterInfo, onClearClick, updateFromServer, isLoading } = useFilterContext();
    let totalBreadCrumbs = 0;
    for (const key in selectedFilterInfo) {
        totalBreadCrumbs += selectedFilterInfo[key].breadcrumbs.length;
    }

    const [isOverlayLoading, setIsOverlayLoading] = useState(false);
    const [animateIn, setAnimateIn] = useState(false);
    const [animateOut, setAnimateOut] = useState(false);

    const pageManager = usePageManager(gettext("general.filters"));
    const { paginationState, resetPagination } = pageManager;

    useIsomorphicLayoutEffect(() => {
        if (!isOpen) {
            setAnimateIn(false);
            setAnimateOut(false);
        } else {
            document.body.classList.toggle("no-scroll", true);
        }
        return (): void => {
            document.body.classList.remove("no-scroll");
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOverlayLoading && !isLoading) {
            setIsOverlayLoading(false);
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOverlayLoading, isLoading]);

    useEffect(() => {
        // Reset the pagination whenever the overlay closes
        if (!overlayIsOpen) {
            resetPagination();
        }
        // The overlay state is the only important dep
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlayIsOpen]);

    useEffect(() => {
        if (!overlayIsOpen) {
            updateFromServer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationState]);

    let appElement: HTMLElement | undefined;
    if (document.body) {
        appElement = document.body.querySelector<HTMLDivElement>(".site-wrapper") || undefined;
    }

    function onClear(): void {
        onClearClick();
        if (!filterBreadcrumb.some((filter) => filter.checked)) {
            cancelStateUpdate();
        }
    }

    function onCancel(): void {
        cancelStateUpdate();
        if (filterBreadcrumb.length > 0) {
            cancelAndClose();
        }
        onClose();
    }

    function onApply(): void {
        setIsOverlayLoading(true);
        updateFromServer();
        applyAndClose();
    }

    function onClose(): void {
        setAnimateOut(true);
        setTimeout(function () {
            closeCollapsable();
        }, 400);
    }

    function onAfterOpen(): void {
        openFeedsFiltersOverlay();
        setAnimateIn(true);
    }

    if (!isOpen) return null;
    return (
        <LazyModal
            appElement={appElement}
            className={clsx(styles.stickyModal, {
                [styles.animateIn]: animateIn,
                [styles.animateOut]: animateOut,
            })}
            isOpen={isOpen}
            onAfterOpen={onAfterOpen}
            onRequestClose={onCancel}
            overlayClassName={styles.overlay}
            shouldCloseOnOverlayClick
            shouldCloseOnEsc
        >
            <>
                {(isLoading || isOverlayLoading) && (
                    <div className={styles.spinner}>
                        <LoadingSpinner />
                    </div>
                )}
                <div className={styles.headerWrapper}>
                    <div
                        className={clsx(styles.titleWrapper, {
                            [styles.titleWrapperMoreFilters]: isMoreFiltersSelected,
                        })}
                    >
                        {isTabletViewport && (
                            <span className={styles.chevron}>
                                <button
                                    data-test-id="close-filters"
                                    onClick={onCancel}
                                    className={styles.chevronButton}
                                    type="button"
                                >
                                    <SVGIcon name="chevron-thin" rotate={90} />
                                </button>
                            </span>
                        )}
                        <Text textStyle="footnote" className={styles.headerTitle}>
                            {sectionTitle}
                        </Text>
                        {isMoreFiltersSelected && totalBreadCrumbs > 0 && (
                            <Text textStyle="footnote" className={styles.headerTitleCount}>
                                {totalBreadCrumbs}
                            </Text>
                        )}
                        {filterBreadcrumb.length > 0 && (
                            <Counter
                                number={filterBreadcrumb.length}
                                iconCounterVariant={"active"}
                            />
                        )}
                        {!isMoreFiltersSelected && (
                            <ClearFilterButton
                                active={filterBreadcrumb.length !== 0}
                                onClick={() => {
                                    if (filterBreadcrumb.length > 0) onClear();
                                }}
                            />
                        )}
                    </div>
                </div>
                {children}
                <div className={styles.buttonWrapper}>
                    <Button
                        data-test-id="cancel-filters"
                        variant={"secondary"}
                        title={
                            // "Cancel"
                            gettext("feed.filters.mobile_cancel_btn_text")
                        }
                        width="full"
                        height="full"
                        onClick={onCancel}
                    />
                    <Button
                        data-test-id="apply-filters"
                        variant={"primary"}
                        title={
                            // "View items"
                            gettext("feed.filters.view_items_label")
                        }
                        width="full"
                        height="full"
                        onClick={onApply}
                        disabled={!canApply}
                    />
                </div>
            </>
        </LazyModal>
    );
}
